import { InputGroup } from '@/components/Inputs/InputGroup';
import PhoneInput from '@/components/Inputs/PhoneInput';
import { FC, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Button from '../button';
import classNames from 'classnames';

export interface RoomstayBlogCiaaLeadFormProps {
    formId: string;
    title?: string;
    description?: string;
    successMessage?: string;
    autoResetSeconds?: number;
}

const RoomstayBlogCiaaLeadForm: FC<RoomstayBlogCiaaLeadFormProps> = ({ formId, title, description, successMessage, autoResetSeconds }) => {
    const formMethods = useForm({
        mode: 'onChange',
    });

    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState(false);
    const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
        };
    }, []);

    const onSubmit = async () => {
        const values = formMethods.getValues();

        if (resetTimerRef.current) {
            clearTimeout(resetTimerRef.current);
            resetTimerRef.current = null;
        }

        setSuccess(false);
        setSending(true);

        try {
            const response = await fetch('/api/form-submission', {
                method: 'POST',
                body: JSON.stringify({
                    firstname: values.firstname,
                    lastname: values.lastname,
                    '0-2/park_name': values['0-2/park_name'],
                    email: values.email,
                    number: values.number,
                }),
            });

            setSuccess(response.ok);
            setSending(false);

            if (autoResetSeconds && autoResetSeconds > 0) {
                resetTimerRef.current = setTimeout(() => {
                    setSuccess(false);
                    formMethods.reset({
                        firstname: '',
                        lastname: '',
                        '0-2/park_name': '',
                        email: '',
                        number: '',
                    });
                }, autoResetSeconds * 1000);
            } else {
                formMethods.reset({
                    firstname: '',
                    lastname: '',
                    '0-2/park_name': '',
                    email: '',
                    number: '',
                });
            }
        } catch (ex) {
            setSending(false);
        }
    };

    return (
        <div className="--theme-light">
            <div className="container">
                <div className="row justify-content-center">
                    {!success ? (
                        <div className="col-md-6 u-marg-bottom--largest">
                            {title && (
                                <div className={classNames('headline --primary-gradient u-text-center', { 'u-marg-bottom': !!description })}>
                                    <h1 style={{ fontSize: '0.75em', lineHeight: 1.1, margin: 0 }}>{title}</h1>
                                </div>
                            )}
                            {description && <p className="body-text u-text-center u-marg-top">{description}</p>}
                        </div>
                    ) : null}
                </div>
                <div className="row justify-content-center">
                    <div className="col-md-6 col-lg-5 col-xl-4">
                        {!success ? (
                            <form id={formId?.trim()?.replace(' ', '-')} onSubmit={formMethods.handleSubmit(onSubmit)}>
                                <div className="helpdesk-inputs u-marg-botto">
                                    <div className="u-marg-bottom">
                                        <InputGroup dimensions="2/2">
                                            <input
                                                type="text"
                                                required
                                                className="text-input"
                                                placeholder="First Name"
                                                tabIndex={1}
                                                {...formMethods.register('firstname', { required: true })}
                                            />
                                            <input
                                                type="text"
                                                required
                                                className="text-input"
                                                placeholder="Last Name"
                                                tabIndex={2}
                                                {...formMethods.register('lastname', { required: true })}
                                            />
                                        </InputGroup>
                                    </div>
                                    <div className="u-marg-bottom">
                                        <input
                                            type="text"
                                            required
                                            className="text-input"
                                            placeholder="Park Name"
                                            tabIndex={3}
                                            {...formMethods.register('0-2/park_name', { required: true })}
                                        />
                                    </div>
                                    <div className="u-marg-bottom">
                                        <input
                                            type="email"
                                            required
                                            className="text-input"
                                            placeholder="Your Email"
                                            tabIndex={4}
                                            {...formMethods.register('email', { required: true })}
                                        />
                                    </div>
                                    <div className="u-marg-bottom">
                                        <Controller
                                            name="phone"
                                            control={formMethods.control}
                                            rules={{ required: true }}
                                            render={({ field }) => (
                                                <PhoneInput
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    defaultCountry="au"
                                                    placeholder="Your Phone Number"
                                                    tabIndex={5}
                                                    required
                                                />
                                            )}
                                        />
                                    </div>
                                </div>

                                <div className="d-flex row justify-content-center align-items-center">
                                    <Button
                                        type="primary"
                                        loading={sending}
                                        disabled={sending}
                                        htmlType="submit"
                                        size="large"
                                        onSubmit={async () => {
                                            await formMethods.handleSubmit(onSubmit)();
                                        }}
                                        style={{ flexShrink: 1, display: 'flex', whiteSpace: 'nowrap', width: 'fit-content' }}
                                    >
                                        Submit
                                    </Button>
                                </div>
                            </form>
                        ) : (
                            <div className="u-text-center">
                                <strong className="body-text --purple --heavy">{successMessage || 'Thanks! Message sent.'}</strong>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RoomstayBlogCiaaLeadForm;
